import {useState} from 'react'
/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react'

import {withFormik, Form, Field} from 'formik'
import * as Yup from 'yup'

import SignUp from '../../registration/SignUp'

import {
  Errors,
  FormButton,
  FormikTextField,
  SlideTransition,
  FormikPhoneNumberField
} from '../../generics'

import Dialog from '@mui/material/Dialog'


const styles = {
  root: css`
    width: 100%;
    display: flex;
    justify-content: center;
  `,
  form: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `,
  formContainerInfo: css`
    font-size: 0.9rem;
    padding-bottom: 10px;
  `,
  signupButtonContainer: css`
    display: flex;
    flex-direction: column;
    gap: 5px;
    button {    
      background: none;
      border: none;
      padding: 0;
      color: #ff0095;
      text-decoration: none;
      cursor: pointer;
      text-transform: uppercase;
      font-weight: 500;
      font-size: 0.9rem;
    }
  `,
  field: css`
    & .MuiInputLabel-root {
      color: rgba(240, 240, 240, 0.7);
      font-size: 14px;
      display: flex;
      padding-left: 4px;
      justify-content: flex-start;
      margin-bottom: 3px;

      &.Mui-focused:not(.Mui-error) {
        color: white;
      }
    }

    & .MuiInputBase-root.MuiFilledInput-root {
      background-color: rgba(0, 128, 255, 0.15);
      border-radius: 4px;
      border: none;
      box-shadow: inset 0 0 0 1px rgba(240, 240, 240, 0.3);

      &:hover:not(.Mui-disabled) {
        background-color: rgba(0, 128, 255, 0.2);
        border: 1px;
      }

      &.Mui-focused:not(.Mui-error) {
        background-color: rgba(0, 128, 255, 0.25);
        border: 1px;
      }
    }

    & .MuiFilledInput-root {
      background-color: rgba(0, 128, 255, 0.15);
      border-radius: 4px;
      border: none;
      box-shadow: inset 0 0 0 1px rgba(240, 240, 240, 0.3);

      &:hover:not(.Mui-disabled) {
        background-color: rgba(0, 128, 255, 0.15);
      }

      &.Mui-focused:not(.Mui-error) {
        background-color: rgba(0, 128, 255, 0.15);
        border: 1px;
      }
    }

    /* style the actual <input> inside a filled TextField */
    & .MuiFilledInput-input {
      color: #fff;
      padding: 8px 16px;
      font-size: 16px;
      border-radius: 4px;
      background-color: rgba(0, 128, 255, 0);

      &:hover {
        background-color: rgba(0, 128, 255, 0.15);
      }

      /* when the filled input is focused */
      &.Mui-focused {
        background-color: rgba(0, 128, 255, 0.15);
      }
    }
  `,
  signUpContainer: css`
    display: flex;
    justify-content: center;
    width: 100%;
  `
}



const RSVPForm = ({isAuthenticated, errors, values, touched, isSubmitting, onSubmit}) => {

  const [signUpOpen, setSignUpOpen] = useState(false)


  function handleSignUpClick(e) {
    setSignUpOpen(true)
  }


  function close() {
    setSignUpOpen(false)
  }


  function signupCallback() {
    onSubmit({})
  }



  return (
    <div css={styles.root}>
      <Form css={styles.form}>
        <div css={styles.formContainerInfo}>
          {isAuthenticated && <p>Add a comment for the host and others to see</p>}
          {!isAuthenticated && 
            <div css={styles.signupButtonContainer}>
              <p>RSVP by entering your email or phone below. All other fields are optional.</p>
              <p css={css`font-weight: 500`}>OR</p>
              <button type='button' onClick={handleSignUpClick}>
                Sign Up
              </button>
            </div>
          }
        </div>

        <Errors errors={errors.unmatched || errors.phone} />

        {!isAuthenticated &&
          <>
            <Field
              css={styles.field}
              name='name'
              label='Name'
              variant='filled'
              fullWidth
              component={FormikTextField}
              placeholder='Name'
              InputLabelProps={{shrink: true}}
            />

            <Field
              css={styles.field}
              name='email'
              label='Email'
              variant='filled'
              fullWidth
              placeholder='Email'
              component={FormikTextField}
              InputLabelProps={{shrink: true}}
            />

            <Field
              css={styles.field}
              name='phone'
              label='Phone'
              component={FormikPhoneNumberField}
              fullWidth
              helperText=''
              variant='filled'
              InputLabelProps={{shrink: true}}
            />
          </>
        }

        <Field
          css={styles.field}
          name='comment'
          label='Comment'
          variant='filled'
          fullWidth
          component={FormikTextField}
          placeholder="Can't wait!"
          InputLabelProps={{shrink: true}}
        />

        <FormButton 
          type='submit' 
          variant='contained'
          sx={{
            '&.MuiButton-root.MuiButton-contained': {
              fontWeight: '600',
              color: 'rgb(62, 166, 255)',
              textTransform: 'uppercase',
              fontSize: '0.9rem'
            }
          }}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </FormButton>
      </Form>


      <Dialog 
        open={signUpOpen} 
        onClose={close} 
        fullWidth
        TransitionComponent={SlideTransition}
        PaperProps={{
          style: { 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '360px' 
          }
        }}
      >
        <div css={styles.signUpContainer}>
          <SignUp close={close} callback={signupCallback} />
        </div>
      </Dialog>
    </div>
  )
}



export default withFormik({
  enableReinitialize: true,
  validateOnChange: false,
  validateOnBlur:   false,

  mapPropsToValues: ({isAuthenticated, formData}) => {
    if (isAuthenticated) {
      return {comment: formData.comment || ''}
    }
    return {
      name:    formData.name    || '',
      email:   formData.email   || '',
      phone:   formData.phone   || '',
      comment: formData.comment || ''
    }
  },

  validationSchema: ({isAuthenticated}) => {
    const commentRule = Yup.string().max(250, 'Must be 250 characters or less')

    if (isAuthenticated) {
      return Yup.object().shape({comment: commentRule})
    }

    // Non‑auth users must supply name (optional), email or phone, plus comment
    return Yup.object({
      name:  Yup.string().max(150, 'Must be 150 characters or less'),
      email: Yup.string().email('Invalid email'),
      phone: Yup.string().test(
        'emailOrPhone',
        'Either email or phone is required',
        function (phone) {
          // this.parent === the other fields
          const {email} = this.parent
          return Boolean(phone || email)
        }
      ),
      comment: commentRule
    })
  },

  handleSubmit: async (values, {props, setSubmitting, setErrors, resetForm}) => {
    try {
      await props.onSubmit(values)
    } catch (error) {
      setErrors(
        error.apiFormErrors
          ? error.apiFormErrors(values)
          : { unmatched: ['Submission failed'] }
      )
    } finally {
      setSubmitting(false)
    }
  }
})(RSVPForm)




